import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Edit, Refresh, History, ReceiptLong } from '@mui/icons-material';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import { useSnackbar } from '../../contexts/SnackbarContext';
import {
  countyBillingService,
  CountyBillingConfig,
} from '../../services/county-billing.service';
import {
  BillingConfigForm,
  BillingFormValue,
  emptyBilling,
  serializeBilling,
  validateBilling,
} from '../../components/billing/BillingConfigForm';

const statusColor: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  expired: 'error',
  suspended: 'warning',
  replaced: 'default',
};

export function CountyBillingPage() {
  const { id } = useParams<{ id: string }>();
  const countyId = Number(id);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [editorOpen, setEditorOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [extendNote, setExtendNote] = useState('');
  const [settlementStart, setSettlementStart] = useState('');
  const [settlementEnd, setSettlementEnd] = useState('');
  const [billingForm, setBillingForm] = useState<BillingFormValue>(emptyBilling());

  const billingQuery = useQuery({
    queryKey: ['county-billing', countyId],
    queryFn: () => countyBillingService.get(countyId),
    enabled: Number.isFinite(countyId),
  });

  const settlementsQuery = useQuery({
    queryKey: ['county-billing-settlements', countyId],
    queryFn: () => countyBillingService.listSettlements(countyId),
    enabled: Number.isFinite(countyId),
  });

  const active = billingQuery.data?.data?.active ?? null;
  const history = billingQuery.data?.data?.history ?? [];

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      countyBillingService.create(countyId, payload),
    onSuccess: () => {
      showSnackbar('Billing configuration saved', 'success');
      setEditorOpen(false);
      queryClient.invalidateQueries({ queryKey: ['county-billing', countyId] });
    },
    onError: (e: any) => {
      showSnackbar(e?.response?.data?.message || 'Failed to save billing', 'error');
    },
  });

  const extendMutation = useMutation({
    mutationFn: () => countyBillingService.extend(countyId, extendDate, extendNote || undefined),
    onSuccess: () => {
      showSnackbar('Subscription extended', 'success');
      setExtendOpen(false);
      setExtendDate('');
      setExtendNote('');
      queryClient.invalidateQueries({ queryKey: ['county-billing', countyId] });
    },
    onError: (e: any) => {
      showSnackbar(e?.response?.data?.message || 'Failed to extend', 'error');
    },
  });

  const settlementMutation = useMutation({
    mutationFn: () =>
      countyBillingService.generateSettlement(countyId, settlementStart, settlementEnd),
    onSuccess: () => {
      showSnackbar('Settlement generated', 'success');
      setSettlementStart('');
      setSettlementEnd('');
      queryClient.invalidateQueries({ queryKey: ['county-billing-settlements', countyId] });
    },
    onError: (e: any) => {
      showSnackbar(e?.response?.data?.message || 'Failed to generate settlement', 'error');
    },
  });

  const openEditor = (preset?: CountyBillingConfig | null) => {
    if (preset) {
      setBillingForm({
        type: preset.type,
        starts_at: preset.starts_at || new Date().toISOString().slice(0, 10),
        ends_at: preset.ends_at || '',
        is_trial: !!preset.is_trial,
        subscription_amount: preset.subscription_amount ? Number(preset.subscription_amount) : '',
        subscription_currency: preset.subscription_currency || 'KES',
        service_fee_mode: preset.service_fee_mode || 'percentage',
        service_fee_percentage: preset.service_fee_percentage
          ? Number(preset.service_fee_percentage)
          : '',
        service_fee_tiers:
          preset.service_fee_tiers && preset.service_fee_tiers.length > 0
            ? preset.service_fee_tiers.map((t) => ({ min: t.min, max: t.max ?? '', fee: t.fee }))
            : emptyBilling().service_fee_tiers,
        revenue_share_provider_percentage: preset.revenue_share_provider_percentage
          ? Number(preset.revenue_share_provider_percentage)
          : 20,
        revenue_share_county_percentage: preset.revenue_share_county_percentage
          ? Number(preset.revenue_share_county_percentage)
          : 80,
        notes: preset.notes || '',
      });
    } else {
      setBillingForm(emptyBilling());
    }
    setEditorOpen(true);
  };

  const submitEditor = () => {
    const err = validateBilling(billingForm);
    if (err) {
      showSnackbar(err, 'error');
      return;
    }
    createMutation.mutate(serializeBilling(billingForm));
  };

  if (billingQuery.isLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="County Billing"
          subtitle="Manage how this county is billed for using the platform"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Counties', path: '/superadmin/counties' },
            { label: 'Billing', path: `/superadmin/counties/${countyId}/billing` },
          ]}
        />

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight={700}>
                    Active configuration
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {active?.type === 'subscription' && (
                      <Button
                        startIcon={<Refresh />}
                        variant="outlined"
                        onClick={() => {
                          setExtendDate(active.ends_at || '');
                          setExtendOpen(true);
                        }}
                      >
                        {active.is_trial ? 'Extend trial' : 'Extend'}
                      </Button>
                    )}
                    <Button startIcon={<Edit />} variant="contained" onClick={() => openEditor(active)}>
                      {active ? 'Change' : 'Set up'}
                    </Button>
                  </Stack>
                </Stack>

                {!active && (
                  <Alert severity="warning">
                    No billing configuration has been set for this county. Citizens can still pay, but no
                    convenience fee or revenue share is being captured. Click &ldquo;Set up&rdquo; to configure.
                  </Alert>
                )}

                {active && <ConfigSummary config={active} />}
              </CardContent>
            </Card>

            {active?.type === 'revenue_share' && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Generate settlement batch
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Aggregates all unsettled completed transactions in the chosen period.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Period start"
                        InputLabelProps={{ shrink: true }}
                        value={settlementStart}
                        onChange={(e) => setSettlementStart(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Period end"
                        InputLabelProps={{ shrink: true }}
                        value={settlementEnd}
                        onChange={(e) => setSettlementEnd(e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={!settlementStart || !settlementEnd || settlementMutation.isPending}
                        onClick={() => settlementMutation.mutate()}
                      >
                        Generate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <History />
                  <Typography variant="h6" fontWeight={700}>
                    History
                  </Typography>
                </Stack>
                {history.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No history yet.
                  </Typography>
                )}
                {history.map((h) => (
                  <Paper key={h.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600} textTransform="capitalize">
                            {h.type.replace('_', ' ')}
                          </Typography>
                          {h.is_trial && (
                            <Chip size="small" color="success" label="Trial" sx={{ fontWeight: 700, height: 20 }} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(h.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Chip size="small" label={h.status} color={statusColor[h.status] || 'default'} />
                    </Stack>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <ReceiptLong />
                  <Typography variant="h6" fontWeight={700}>
                    Settlements
                  </Typography>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell align="right">Gross</TableCell>
                        <TableCell align="right">Provider share</TableCell>
                        <TableCell align="right">County share</TableCell>
                        <TableCell align="right">Txns</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(settlementsQuery.data?.data?.data || []).map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            {s.period_start} → {s.period_end}
                          </TableCell>
                          <TableCell align="right">{Number(s.gross_collected).toLocaleString()}</TableCell>
                          <TableCell align="right">
                            {Number(s.provider_share_total).toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {Number(s.county_share_total).toLocaleString()}
                          </TableCell>
                          <TableCell align="right">{s.transaction_count}</TableCell>
                          <TableCell>
                            <Chip size="small" label={s.status} />
                          </TableCell>
                        </TableRow>
                      ))}
                      {!settlementsQuery.data?.data?.data?.length && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="text.secondary" align="center">
                              No settlements yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configure billing</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <BillingConfigForm value={billingForm} onChange={setBillingForm} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button onClick={submitEditor} variant="contained" disabled={createMutation.isPending}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={extendOpen} onClose={() => setExtendOpen(false)}>
        <DialogTitle>{active?.is_trial ? 'Extend free trial' : 'Extend subscription'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, minWidth: 360 }}>
            <TextField
              type="date"
              label="New end date"
              InputLabelProps={{ shrink: true }}
              value={extendDate}
              onChange={(e) => setExtendDate(e.target.value)}
            />
            <TextField
              multiline
              minRows={2}
              label="Notes (optional)"
              value={extendNote}
              onChange={(e) => setExtendNote(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!extendDate || extendMutation.isPending}
            onClick={() => extendMutation.mutate()}
          >
            Extend
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}

function ConfigSummary({ config }: { config: CountyBillingConfig }) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Chip size="small" label={config.status} color={statusColor[config.status] || 'default'} />
        <Typography variant="body1" fontWeight={600} textTransform="capitalize">
          {config.type.replace('_', ' ')}
        </Typography>
        {config.is_trial && (
          <Chip size="small" color="success" label="Free Trial" sx={{ fontWeight: 700 }} />
        )}
      </Stack>

      {config.type === 'subscription' && (
        <Typography variant="body2" color="text.secondary">
          {config.is_trial
            ? <>Trial period {config.starts_at} → <strong>{config.ends_at}</strong> (no cost)</>
            : <>{config.subscription_currency} {Number(config.subscription_amount || 0).toLocaleString()} for period {config.starts_at} → <strong>{config.ends_at}</strong></>}
        </Typography>
      )}

      {config.type === 'service_fee' && config.service_fee_mode === 'percentage' && (
        <Typography variant="body2" color="text.secondary">
          {config.service_fee_percentage}% added on top of every transaction
        </Typography>
      )}

      {config.type === 'service_fee' && config.service_fee_mode === 'tiered' && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Fee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(config.service_fee_tiers || []).map((t, i) => (
                <TableRow key={i}>
                  <TableCell>{t.min}</TableCell>
                  <TableCell>{t.max ?? '∞'}</TableCell>
                  <TableCell>{t.fee}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {config.type === 'revenue_share' && (
        <Typography variant="body2" color="text.secondary">
          Provider <strong>{config.revenue_share_provider_percentage}%</strong> · County{' '}
          <strong>{config.revenue_share_county_percentage}%</strong>
        </Typography>
      )}

      {config.notes && (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
          {config.notes}
        </Typography>
      )}
    </Stack>
  );
}
